/**
 * File Storage Service - Local file persistence using IndexedDB
 *
 * Stores documents, audio recordings, images, and other binary files locally.
 * Provides a simple API for CRUD operations on files.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface StoredFile {
  id: string;
  name: string;
  type: string; // MIME type
  size: number;
  data: Blob;
  createdAt: string;
  metadata?: {
    transcription?: string; // For audio files
    searchResults?: any; // For web search results
    duration?: number; // For audio/video
    width?: number; // For images
    height?: number; // For images
    [key: string]: any;
  };
}

export interface FileReference {
  id: string;
  name: string;
  type: string;
  size: number;
  createdAt: string;
}

// =============================================================================
// INDEXEDDB CONFIGURATION
// =============================================================================

const DB_NAME = 'business-assistant-files';
const DB_VERSION = 1;
const STORE_NAME = 'files';

// =============================================================================
// DATABASE INITIALIZATION
// =============================================================================

let dbInstance: IDBDatabase | null = null;

async function getDatabase(): Promise<IDBDatabase> {
  if (dbInstance) {
    return dbInstance;
  }

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error(`Failed to open database: ${request.error?.message}`));
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create files store
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex('name', 'name', { unique: false });
        store.createIndex('type', 'type', { unique: false });
        store.createIndex('createdAt', 'createdAt', { unique: false });
      }
    };
  });
}

// =============================================================================
// FILE OPERATIONS
// =============================================================================

/**
 * Save a file to local storage
 */
export async function saveFile(
  file: File | Blob,
  options?: {
    id?: string;
    name?: string;
    metadata?: StoredFile['metadata'];
  }
): Promise<StoredFile> {
  const db = await getDatabase();

  const id = options?.id || `file-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const name = options?.name || (file instanceof File ? file.name : `file-${id}`);

  const storedFile: StoredFile = {
    id,
    name,
    type: file.type || 'application/octet-stream',
    size: file.size,
    data: file instanceof Blob ? file : new Blob([file]),
    createdAt: new Date().toISOString(),
    metadata: options?.metadata,
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(storedFile);

    request.onsuccess = () => resolve(storedFile);
    request.onerror = () => reject(new Error(`Failed to save file: ${request.error?.message}`));
  });
}

/**
 * Get a file by ID
 */
export async function getFile(id: string): Promise<StoredFile | null> {
  const db = await getDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.get(id);

    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(new Error(`Failed to get file: ${request.error?.message}`));
  });
}

/**
 * Get file URL (creates a blob URL for display/playback)
 */
export async function getFileUrl(id: string): Promise<string | null> {
  const file = await getFile(id);
  if (!file) return null;
  return URL.createObjectURL(file.data);
}

/**
 * Delete a file by ID
 */
export async function deleteFile(id: string): Promise<void> {
  const db = await getDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.delete(id);

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to delete file: ${request.error?.message}`));
  });
}

/**
 * List all files (returns references only, not data)
 */
export async function listFiles(options?: {
  type?: string;
  limit?: number;
  offset?: number;
}): Promise<FileReference[]> {
  const db = await getDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      let files = request.result as StoredFile[];

      // Filter by type if specified
      if (options?.type) {
        files = files.filter(f => f.type.startsWith(options.type!));
      }

      // Sort by creation date (newest first)
      files.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      // Apply pagination
      if (options?.offset) {
        files = files.slice(options.offset);
      }
      if (options?.limit) {
        files = files.slice(0, options.limit);
      }

      // Return references only (without blob data)
      const refs: FileReference[] = files.map(f => ({
        id: f.id,
        name: f.name,
        type: f.type,
        size: f.size,
        createdAt: f.createdAt,
      }));

      resolve(refs);
    };
    request.onerror = () => reject(new Error(`Failed to list files: ${request.error?.message}`));
  });
}

/**
 * Update file metadata
 */
export async function updateFileMetadata(
  id: string,
  metadata: StoredFile['metadata']
): Promise<StoredFile | null> {
  const db = await getDatabase();
  const file = await getFile(id);

  if (!file) return null;

  const updatedFile: StoredFile = {
    ...file,
    metadata: { ...file.metadata, ...metadata },
  };

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.put(updatedFile);

    request.onsuccess = () => resolve(updatedFile);
    request.onerror = () => reject(new Error(`Failed to update file: ${request.error?.message}`));
  });
}

/**
 * Get storage usage statistics
 */
export async function getStorageStats(): Promise<{
  fileCount: number;
  totalSize: number;
  byType: Record<string, { count: number; size: number }>;
}> {
  const db = await getDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const files = request.result as StoredFile[];
      const byType: Record<string, { count: number; size: number }> = {};

      let totalSize = 0;
      for (const file of files) {
        totalSize += file.size;

        const typeCategory = file.type.split('/')[0] || 'other';
        if (!byType[typeCategory]) {
          byType[typeCategory] = { count: 0, size: 0 };
        }
        byType[typeCategory].count++;
        byType[typeCategory].size += file.size;
      }

      resolve({
        fileCount: files.length,
        totalSize,
        byType,
      });
    };
    request.onerror = () => reject(new Error(`Failed to get storage stats: ${request.error?.message}`));
  });
}

/**
 * Clear all files (use with caution!)
 */
export async function clearAllFiles(): Promise<void> {
  const db = await getDatabase();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(new Error(`Failed to clear files: ${request.error?.message}`));
  });
}

// =============================================================================
// SPECIALIZED FILE OPERATIONS
// =============================================================================

/**
 * Save an audio recording with optional transcription
 */
export async function saveAudioRecording(
  audioBlob: Blob,
  transcription?: string
): Promise<StoredFile> {
  const id = `audio-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const name = `recording-${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;

  return saveFile(audioBlob, {
    id,
    name,
    metadata: {
      transcription,
    },
  });
}

/**
 * Save a document (PDF, Word, etc.)
 */
export async function saveDocument(
  file: File
): Promise<StoredFile> {
  const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

  return saveFile(file, {
    id,
    name: file.name,
  });
}

/**
 * Save web search results
 */
export async function saveSearchResults(
  query: string,
  results: any
): Promise<StoredFile> {
  const id = `search-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  const name = `search-${query.slice(0, 50).replace(/[^a-zA-Z0-9]/g, '-')}.json`;

  const blob = new Blob([JSON.stringify({ query, results, timestamp: new Date().toISOString() })], {
    type: 'application/json',
  });

  return saveFile(blob, {
    id,
    name,
    metadata: {
      searchResults: { query, resultCount: results.length || 0 },
    },
  });
}

// =============================================================================
// EXPORT
// =============================================================================

export const fileStorageService = {
  saveFile,
  getFile,
  getFileUrl,
  deleteFile,
  listFiles,
  updateFileMetadata,
  getStorageStats,
  clearAllFiles,
  saveAudioRecording,
  saveDocument,
  saveSearchResults,
};

export default fileStorageService;
